// Hub Messages page (read-only). Composes the embeddable MessageThread with Hub
// data: the conversation timeline from the backend `hub_message` store, membership
// status, the AWO's phone number, and the shared unread signal. The thread sizes to
// this page's bounded height; the page does not impose viewport units on the thread
// itself.
import React, { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { MessageThread } from '../../MessageThread';
import hubLessonPath from './lessonLinks';
import { toThreadMessages } from '../../MessageThread/messageAdapter';
import { TimelineMessage } from '../../MessageThread/types';
import { ComposerSendPayload } from '../../MessageThread/Composer';
import { useHubPetParent } from '../HubPetParentContext';
import { useMessagesUnread } from './MessagesUnreadContext';
import {
  GET_HUB_MESSAGE_TIMELINE, CREATE_HUB_MEDIA_UPLOAD_URLS, SEND_HUB_MESSAGE,
} from '../../../queries';
import { phoneFormatter } from '../../../utilities';
import { Awo } from '../../../types/Awo';
import { track } from '../analytics';
import useHubPageView from '../useHubPageView';

interface HubMessageTimeline {
  unreadCount: number;
  membershipStatus: string | null;
  messages: TimelineMessage[];
}

const UPGRADE_URL = 'https://petcademy.org/pricing/';
const SUPPORT_EMAIL = 'support@petcademy.org';

interface UploadUrl { uploadUrl: string; fileUrl: string; key: string }

function Messages() {
  const { petParent, loading: petParentLoading } = useHubPetParent();
  const { markAllRead } = useMessagesUnread();
  const { data, loading: timelineLoading } = useQuery<{ hubMessageTimeline: HubMessageTimeline }>(
    GET_HUB_MESSAGE_TIMELINE,
    { fetchPolicy: 'cache-and-network' },
  );
  const [createUploadUrls] = useMutation<{ createHubMediaUploadUrls: UploadUrl[] }>(
    CREATE_HUB_MEDIA_UPLOAD_URLS,
  );
  const [sendHubMessage] = useMutation<{ sendHubMessage: TimelineMessage }>(SEND_HUB_MESSAGE);
  const [sendError, setSendError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Lesson links trainers paste (the public URLs from the lessons sheet) open
  // in the SAME tab as the in-hub lesson, so the lesson feels like part of the
  // Hub. `from` makes both the browser Back button and the lesson's own back
  // arrow return here; `fromWhere` attributes the page_view (same pattern as
  // the home checklist). Everything else keeps the thread's default new tab.
  const handleLinkClick = useCallback((href: string) => {
    const path = hubLessonPath(href, window.location.host);
    if (!path) return false;
    navigate(path, { state: { from: '/hub/messages', fromWhere: 'messages' } });
    return true;
  }, [navigate]);

  // Send flow: presign -> direct S3 PUT for each file -> sendHubMessage. On success
  // the persisted message is appended to the timeline cache so it shows immediately
  // (AC4); on failure we surface an error and rethrow so the composer keeps the draft.
  const handleSend = useCallback(async ({ text, media }: ComposerSendPayload) => {
    setSendError(null);
    try {
      let mediaInput: { url: string; fileType: string }[] = [];
      if (media.length) {
        const { data: presign } = await createUploadUrls({
          variables: { files: media.map((m) => ({ filename: m.name, contentType: m.file.type })) },
        });
        const urls = presign?.createHubMediaUploadUrls ?? [];
        if (urls.length !== media.length) throw new Error('Failed to prepare upload.');
        await Promise.all(media.map((m, i) => fetch(urls[i].uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': m.file.type },
          body: m.file,
        }).then((res) => { if (!res.ok) throw new Error('Upload failed.'); })));
        mediaInput = media.map((m, i) => ({ url: urls[i].fileUrl, fileType: m.file.type }));
      }
      await sendHubMessage({
        variables: { text: text || null, media: mediaInput.length ? mediaInput : null },
        update: (cache, { data: res }) => {
          const sent = res?.sendHubMessage;
          if (!sent) return;
          const existing = cache.readQuery<{ hubMessageTimeline: HubMessageTimeline }>({
            query: GET_HUB_MESSAGE_TIMELINE,
          });
          if (!existing?.hubMessageTimeline) return;
          cache.writeQuery({
            query: GET_HUB_MESSAGE_TIMELINE,
            data: {
              hubMessageTimeline: {
                ...existing.hubMessageTimeline,
                messages: [...existing.hubMessageTimeline.messages, sent],
              },
            },
          });
        },
      });
    } catch (e) {
      setSendError('Your message couldn’t be sent. Please check your connection and try again.');
      throw e; // keep the composer draft + attachments
    }
  }, [createUploadUrls, sendHubMessage]);

  useHubPageView();

  const timeline = data?.hubMessageTimeline;
  const messages = useMemo(
    () => toThreadMessages(timeline?.messages ?? []),
    [timeline?.messages],
  );

  if (petParentLoading || timelineLoading) return null;

  // Map the backend membership string to the thread's two-state model. Prefer the
  // timeline's value (computed server-side from the same pet parent), falling back
  // to the Hub pet-parent context.
  const membership = timeline?.membershipStatus ?? petParent?.membershipStatus;
  const membershipStatus = membership === 'active' ? 'active' : 'inactive';
  const unreadCount = timeline?.unreadCount ?? 0;
  // Reuse the shared formatter so the AWO's number is shown in its country's format
  // (e.g. Australian numbers as 0XX XXX XXX). Falls back to the thread's placeholder.
  const contactPhone = phoneFormatter(
    petParent?.awo?.twilioPhoneNumber,
    petParent?.awo?.country as Awo['country'],
  ) || undefined;
  // Newest message the user has loaded (oldest->newest), used as the mark-read
  // watermark so a message arriving after load isn't marked read unseen.
  const newestSentAt = timeline?.messages?.[timeline.messages.length - 1]?.sentAt;

  // The page fills the Hub content area; the thread owns the single (internal)
  // scroll. The "Messages" header is passed into the thread's scroll region so it
  // scrolls away with the conversation, while the contact-note footer stays pinned
  // to the bottom (above the nav) on both desktop and mobile.
  const headerNode = (
    <Box sx={{
      bgcolor: '#fff',
      borderBottom: '1px solid',
      borderColor: 'divider',
      px: { xs: 2, md: 4 },
      py: 2,
    }}
    >
      <Typography variant="sectionTitle" sx={{ fontSize: '1.5rem', lineHeight: 1.1 }}>
        Messages
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
        Your conversation history with Petcademy.
      </Typography>
    </Box>
  );

  return (
    // Definite height (not flex:1) so the thread is bounded and scrolls INTERNALLY
    // with its footer pinned — a min-height-based parent would let it grow to content
    // and window-scroll instead. Mobile reserves the logo bar (~53px) + bottom nav
    // (72px); desktop has no top/bottom chrome. The shell is 100dvh, so this matches
    // the visible viewport exactly (no second, outer scrollbar).
    <Box sx={{ height: { xs: 'calc(100dvh - 125px)', md: '100dvh' }, display: 'flex' }}>
      <MessageThread
        header={headerNode}
        messages={messages}
        membershipStatus={membershipStatus}
        unreadCount={unreadCount}
        contactPhone={contactPhone}
        supportEmail={SUPPORT_EMAIL}
        upgradeUrl={UPGRADE_URL}
        onUpgradeClick={() => track('extend_membership_click', { from_where: 'messages' })}
        onMarkRead={() => markAllRead(newestSentAt)}
        onSend={handleSend}
        sendError={sendError}
        onLinkClick={handleLinkClick}
      />
    </Box>
  );
}

export default Messages;
